'use client'

import { FC, useMemo, ReactNode } from 'react'
import Link from 'next/link'
import cn from 'classnames'
import * as R from 'ramda'
import { DateTime } from 'luxon'

import { useCustomRouter, useGetTranslations } from '@/root/shared-hooks'
import { SailPackagesGroup } from '@/root/shared-types/reservation/reservation-types'
import { SailPackage } from '@/root/shared-types'
import { APP_ROUTES, dateFormats , testIds } from '@/root/shared-consts'

import { PassengerIconBlue } from '@/root/ui/shared/icons/PassengerIconBlue'
import { BottomArrowIcon } from '@/root/ui/shared/icons/BottomArrowIcon'
import { BicycleIcon } from '@/root/ui/shared/icons/BicycleIcon'
import { CarIconBlue } from '@/root/ui/shared/icons/CarIconBlue'
import { TruckIconBlue } from '@/root/ui/shared/icons/TruckIconBlue'

type Props = {
  sailPackagesGroups?: Array<SailPackagesGroupWithAttrs<SailPackage[]>>
  sailPackages?: SailPackage[]
  mapImageSrc: string
  className?: string
}

type RouteItem = {
  id: string
  from: string
  to: string
  href: string
  supportPassengers?: boolean
  supportBicycles?: boolean
  supportCars?: boolean
  supportTrucks?: boolean
  supportVehicles?: boolean
  subtitleFrom?: string
  subtitleTo?: string
}

type SailPackagesGroupWithAttrs<T> = SailPackagesGroup<T> & {
  attributes?: SailPackagesGroupAttributes
}

type SailPackagesGroupAttributes = {
  SUPPORT_PASSENGERS?: boolean
  SUPPORT_BICYCLES?: boolean
  SUPPORT_CARS?: boolean
  SUPPORT_TRUCKS?: boolean
  SUPPORT_VEHICLES?: boolean
}

type IconVariant = 'passenger' | 'bicycle' | 'car' | 'truck'

const ICON_COMPONENTS: Record<IconVariant, ReactNode> = {
  passenger: <PassengerIconBlue width={28} height={28} className="h-7 w-7" />,
  bicycle: <BicycleIcon width={28} height={28} className="h-7 w-7" />,
  car: <CarIconBlue width={28} height={28} className="h-7 w-7" />,
  truck: <TruckIconBlue width={28} height={28} className="h-7 w-7" />,
}

const IconRow: FC<{ route: RouteItem }> = ({ route }) => {
  const iconKeys = useMemo(() => {
    const keyList: Array<IconVariant> = []
    if (route.supportPassengers) keyList.push('passenger')
    if (route.supportBicycles) keyList.push('bicycle')
    if (route.supportCars || route.supportVehicles) keyList.push('car')
    if (route.supportTrucks) keyList.push('truck')
    return keyList
  }, [route])

  return (
    <div className="flex shrink-0 items-center justify-end gap-2">
      {iconKeys.slice(0, 3).map((key) => (
        <span key={key} className="h-7 w-7">
          {ICON_COMPONENTS[key]}
        </span>
      ))}
      {iconKeys[3] && <span className="xxs:inline-flex hidden h-7 w-7">{ICON_COMPONENTS[iconKeys[3]]}</span>}
    </div>
  )
}

export const RouteCardsSection: FC<Props> = ({
  sailPackagesGroups = [],
  sailPackages = [],
  mapImageSrc,
  className,
}) => {
  const { getTranslation } = useGetTranslations()
  const { locale } = useCustomRouter()

  const routes = useMemo<RouteItem[]>(() => {
    const today = DateTime.now().toFormat(dateFormats.ISO_DATE)

    const groupByFirstPackageCode = new Map<string, SailPackagesGroupWithAttrs<SailPackage[]>>()
    sailPackagesGroups.forEach((g) => {
      const firstCode = g.sailPackages?.[0]?.code
      if (firstCode && !groupByFirstPackageCode.has(firstCode)) {
        groupByFirstPackageCode.set(firstCode, g)
      }
    })

    const result: RouteItem[] = []
    const usedGroupCodes = new Set<string>()

    sailPackages.forEach((pkg) => {
      const pkgCode = pkg.code
      const group = groupByFirstPackageCode.get(pkgCode)
      if (!group) return

      if (usedGroupCodes.has(group.code)) return
      usedGroupCodes.add(group.code)

      const compactTitle = (pkg as any)?.title as string | undefined
      const [fromFromCompact, toFromCompact] = compactTitle?.split(' - ').map((s) => s.trim()) ?? []

      const fallbackFrom = R.pathOr('', [locale], group.from)
      const fallbackTo = R.pathOr('', [locale], group.to)

      const from = fromFromCompact || fallbackFrom
      const to = toFromCompact || fallbackTo

      result.push({
        id: group.code,
        from,
        to,
        subtitleFrom: group.from?.en || '',
        subtitleTo: group.to?.en || '',
        href: `/${locale}/${APP_ROUTES.selectDateSail}/${encodeURIComponent(pkgCode)}/${encodeURIComponent(today)}`,
        supportPassengers: Boolean(group.features?.supportPassengers),
        supportBicycles: Boolean(group.features?.supportBicycles ?? group.attributes?.SUPPORT_BICYCLES),
        supportCars: Boolean(group.features?.supportCars),
        supportTrucks: Boolean(group.features?.supportTrucks),
        supportVehicles: Boolean(group.features?.supportVehicles ?? group.attributes?.SUPPORT_VEHICLES),
      })
    })

    return result.filter((route) => route.from && route.to && route.href.includes('/')).slice(0, 6)
  }, [sailPackagesGroups, sailPackages, locale])

  return (
    <section className={cn('mx-auto w-full max-w-7xl py-8', className)}>
      <h1 className="text-primary-base mb-6 text-center text-2xl font-bold">
        {getTranslation('mainViewRouteSelectionHeader')}
      </h1>

      <div className="flex w-full gap-5 lg:justify-center">
        <div className="w-full rounded-2xl bg-white px-4 py-3 shadow-sm lg:w-xl">
          <ul className="flex h-full flex-col">
            {routes.map((route, idx) => (
              <li key={route.id} className="flex flex-col">
                {idx !== 0 && <div className="border-neutral-normal-light/70 mx-2 border-b" />}

                <Link
                  href={route.href}
                  data-testid={testIds.search.route}
                  className="group hover:bg-neutral-normal-light/30 flex min-h-15 items-center rounded-xl px-2 py-2 transition"
                >
                  <div className="grid w-full min-w-0 grid-cols-[80px_45px_minmax(0,1fr)_auto] items-center gap-3 md:grid-cols-[160px_50px_minmax(0,1fr)_auto] md:gap-5 lg:grid-cols-[160px_50px_minmax(0,1fr)_auto] lg:gap-5">
                    <div className="min-w-0">
                      <div className="text-primary-base truncate pb-1 text-lg leading-5 font-bold">{route.from}</div>
                      {route.subtitleFrom && (
                        <div className="text-neutral-medium-lighter truncate text-sm leading-4">
                          {route.subtitleFrom}
                        </div>
                      )}
                    </div>

                    <div className="flex w-11.25 items-center justify-center text-neutral-400 md:w-7">
                      <BottomArrowIcon className="h-5 w-5 -rotate-90" />
                    </div>

                    <div className="min-w-0">
                      <div className="text-primary-base truncate pb-1 text-lg leading-5 font-bold">{route.to}</div>
                      {route.subtitleTo && (
                        <div className="text-neutral-medium-lighter truncate text-sm leading-4">{route.subtitleTo}</div>
                      )}
                    </div>

                    <div className="shrink-0 justify-self-end">
                      <IconRow route={route} />
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="hidden w-full rounded-2xl bg-white shadow-sm lg:block lg:w-xl">
          <img src={mapImageSrc} alt="Routes map" className="h-full w-full rounded-xl object-cover" />
        </div>
      </div>
    </section>
  )
}
